import { merge } from '@/helpers/Utility';
import { FC, useCallback, useEffect, useState } from 'react';
import { IconType } from 'react-icons';
import { BsBarChartFill } from 'react-icons/bs';
import { FiAlertTriangle, FiChevronDown, FiChevronUp, FiClipboard } from 'react-icons/fi';
import { useAllWarnings } from '../providers/RosterWarningsService';
import strings from '../roster.translations';
import { RosterSummary } from './RosterSummary';
import { RosterTaskButtons } from './RosterTaskButtons';
import { RosterWarnings } from './RosterWarnings';

enum RosterDrawerState {
  NONE,
  SUMMARY,
  TASKS,
  WARNINGS
}

const RosterDrawerButton: FC<{ name: string; Icon: IconType; onClick: () => void; active: boolean }> = ({
  name,
  Icon,
  onClick,
  active
}) => {
  return (
    <button
      onClick={onClick}
      className={merge(
        'text-xl py-1 px-5 flex gap-2 items-center rounded-full border border-2 font-semibold',
        active ? 'bg-primary-100 text-primary-500 border-primary-500' : 'bg-white text-gray-700 border-gray-700'
      )}
    >
      <Icon className="h-6 w-6" />
      {name}
    </button>
  );
};

export const RosterDrawer: FC = () => {
  const [state, setState] = useState<RosterDrawerState>(RosterDrawerState.NONE);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);

  useEffect(() => {
    if (drawerOpen && state === RosterDrawerState.NONE) {
      setState(RosterDrawerState.TASKS);
    }
  }, [drawerOpen, state]);

  const updateState = useCallback(
    (newState: RosterDrawerState) => {
      if (drawerOpen && newState === state) {
        setDrawerOpen(false);
      } else {
        setDrawerOpen(true);
        setState(newState);
      }
    },
    [setState, setDrawerOpen, drawerOpen, state]
  );

  const rosterWarnings = useAllWarnings();
  const warningsCount = rosterWarnings.length;

  return (
    <div className="border-t bg-white">
      <div className="flex justify-between items-center border-b px-5 py-2">
        <div className="flex gap-5">
          <RosterDrawerButton
            name={strings.drawer.summary.label}
            Icon={BsBarChartFill}
            onClick={() => updateState(RosterDrawerState.SUMMARY)}
            active={drawerOpen && state === RosterDrawerState.SUMMARY}
          />
          <RosterDrawerButton
            name={strings.drawer.tasks.label}
            Icon={FiClipboard}
            onClick={() => updateState(RosterDrawerState.TASKS)}
            active={drawerOpen && state === RosterDrawerState.TASKS}
          />
          <RosterDrawerButton
            name={`${strings.drawer.warnings.label}` + (warningsCount > 0 ? ` (${warningsCount})` : '')}
            Icon={FiAlertTriangle}
            onClick={() => updateState(RosterDrawerState.WARNINGS)}
            active={drawerOpen && state === RosterDrawerState.WARNINGS}
          />
        </div>
        <button onClick={() => setDrawerOpen(!drawerOpen)}>
          {drawerOpen ? <FiChevronDown className="h-10 w-10" /> : <FiChevronUp className="h-10 w-10" />}
        </button>
      </div>
      {drawerOpen && (
        <div className="h-40 py-2 px-5 overflow-auto">
          {state === RosterDrawerState.SUMMARY && <RosterSummary />}
          {state === RosterDrawerState.TASKS && <RosterTaskButtons />}
          {state === RosterDrawerState.WARNINGS && <RosterWarnings />}
        </div>
      )}
    </div>
  );
};
